import React, { useEffect, useState } from "react";
import { Button, Modal, Table } from "antd";
import SalesService from "../../../services/Sales.services"; // your API service
import { useSelector } from "react-redux";
import { IInitialState } from "../../../store/reducers/models";
import { useNavigate } from "react-router-dom"; // ✅ Import navigate

interface ReminderProps {
  visible: boolean;
  onClose: () => void;
}

export default function GlobalFollowUpReminder({
  visible,
  onClose,
}: ReminderProps) {
  const [followups, setFollowups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [reminders, setReminders] = useState<string[]>([]);
  const gloablStore = useSelector((state: any) => state.store);
  const { followUpData }: IInitialState = gloablStore;
  const [localFollowUpData, setLocalFollowUpData] = useState(false);
  const navigate = useNavigate(); // ✅ navigation hook

  const userDetails = localStorage.getItem("user_details") || {};

  useEffect(() => {
    if (Object.keys(userDetails).length > 0) {
      // Function to sync local state with global followUpData
      const syncFollowUps = () => {
        setLocalFollowUpData(followUpData);
      };

      // Call immediately on mount
      syncFollowUps();

      // Set interval to call every 5 minutes (5 * 60 * 1000 ms)
      const intervalId = setInterval(syncFollowUps, 300000);

      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [userDetails, followUpData]);

  const onClosePopUp = () => {
    onClose();
    setLocalFollowUpData(false); // or [] if you want an empty array
  };

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const response = await SalesService.getsalesList(
        `/sales/follow-ups?notification=true`
      );

      const data = response.data || [];
      setFollowups(data);
      const hasFollowUp30Min = data.some((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return false;

        const currentDate = new Date(item.curdatetime);
        const followUpDate = new Date(item.follow_up_date);

        const diffMinutes =
          (followUpDate.getTime() - currentDate.getTime()) / 60000;

        return diffMinutes >= 0 && diffMinutes <= 30;
      });

      const hasFollowUp15Min = data.some((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return false;

        const currentDate = new Date(item.curdatetime);
        const followUpDate = new Date(item.follow_up_date);

        const diffMinutes =
          (followUpDate.getTime() - currentDate.getTime()) / 60000;

        return diffMinutes >= 0 && diffMinutes <= 15;
      });

      const hasFollowUp10Min = data.some((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return false;

        const currentDate = new Date(item.curdatetime);
        const followUpDate = new Date(item.follow_up_date);

        const diffMinutes =
          (followUpDate.getTime() - currentDate.getTime()) / 60000;

        return diffMinutes >= 0 && diffMinutes <= 10;
      });

      const hasFollowUp5Min = data.some((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return false;

        const currentDate = new Date(item.curdatetime);
        const followUpDate = new Date(item.follow_up_date);

        const diffMinutes =
          (followUpDate.getTime() - currentDate.getTime()) / 60000;

        return diffMinutes >= 0 && diffMinutes <= 5;
      });

      const newReminders: string[] = [];

      data.forEach((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return;

        const currentDate = new Date(item.curdatetime);
        const followUpDate = new Date(item.follow_up_date);

        let diffMinutes =
          (followUpDate.getTime() - currentDate.getTime()) / 60000;
        diffMinutes = parseFloat(diffMinutes.toFixed(2));

        if (
          diffMinutes > 0 &&
          (diffMinutes <= 15 || diffMinutes <= 10 || diffMinutes <= 5)
        ) {
          newReminders.push(
            `⏳ You have an appointment with ${item.name} in ${diffMinutes} minutes`
          );
        }
      });

      setReminders(newReminders);
    } catch (err) {
      console.error("Reminder check failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(userDetails).length > 0) {
      fetchFollowups();

      // auto-refresh every 30s
      const timer = setInterval(fetchFollowups, 300000);
      return () => clearInterval(timer);
    }
  }, [userDetails, followUpData]);

  // ✅ Table column definitions
  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "phone_number", key: "phone_number" },
    {
      title: "Follow Up Date",
      dataIndex: "follow_up_date",
      key: "follow_up_date",
      render: (date: any) =>
        new Date(date).toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "UTC", // ✅ stays in UTC (or change to "America/Denver" for MST)
        }),
    },
    { title: "Comment", dataIndex: "COMMENT", key: "COMMENT" },
    { title: "Timezone", dataIndex: "timezone", key: "timezone" },
  ];

  const handleAction = () => {
    setLocalFollowUpData(false);
    navigate("/sales/followup-calls"); // ✅ navigate on Action
    onClose();
  };

  if (!visible || !followups || followups.length === 0) {
    return null;
  }

  return (
    <Modal
      title="Today Follow Up Appointments"
      open={localFollowUpData || visible}
      onCancel={onClosePopUp}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
          <Button onClick={onClosePopUp}>Got It</Button>
          <Button
            type="primary"
            onClick={handleAction}
            style={{ backgroundColor: "green", borderColor: "green" }}
          >
            Action
          </Button>
        </div>
      }
      width={800}
    >
      <Table
        dataSource={followups}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      {/* ✅ Dynamic reminders */}
      {reminders.length > 0 ? (
        reminders.map((msg, idx) => (
          <p
            key={idx}
            style={{
              marginTop: "16px",
              color: "red",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {msg}
          </p>
        ))
      ) : (
        <span style={{ color: "#fff" }}>.</span>
      )}
    </Modal>
  );
}
